import { useEffect, FC } from "react";
import {
  Switch,
  Route,
  NavLink,
  useParams,
  useRouteMatch,
  Redirect,
  NavLinkProps,
} from "react-router-dom";
import Frontpage from "@src/frontpage/Frontpage";
import CompanyDetails from "@src/details/CompanyDetails";
import OrderPage from "@src/OrderPage";
import PermissionPage from "@src/PermissionPage";
import { useAppDispatch } from "@src/reduxStore";
import { changeOrg, toggleOrgContext } from "@src/common/commonSlice";
import { useOrgId, useUserInfo } from "@src/common/reliablePartnerApi";
import { useCheckContacts } from "@src/details/detailApi";
import { ResponsiveFirstLevelNav } from "@src/common/Navigation";
import { RPFormattedMessage } from "@src/i18n";
import AdminNotifications from "@src/AdminNotifications";
import LK2Info from "@src/LK2Info";

export const CurrentOrgLink: FC<NavLinkProps> = ({ to, ...rest }) => {
  const orgId = useOrgId();
  return <NavLink to={`/orgs/${orgId || ""}${to.toString()}`} {...rest} />; // nosemgrep typescript.react.best-practice.react-props-spreading.react-props-spreading
};

const RPNavBar = () => {
  const contactsOk = useCheckContacts();

  return (
    <>
      <header>
        <h1>
          <RPFormattedMessage
            id="rpHeader"
            defaultMessage="Luotettava Kumppani: {orgName}"
            description="Sivuston pääotsikko"
          />
        </h1>
      </header>
      <AdminNotifications />
      <LK2Info />
      <ResponsiveFirstLevelNav>
        <li className="nav-item">
          <CurrentOrgLink className="nav-link" id="homeLink" exact to="">
            <RPFormattedMessage id="homeTab" defaultMessage="Etusivu" />
          </CurrentOrgLink>
        </li>
        <li className="nav-item">
          <CurrentOrgLink className="nav-link" id="detailsLink" to={"/details"}>
            <RPFormattedMessage
              id="companyInformation"
              defaultMessage="Yrityksen tiedot"
            />
            {!contactsOk && (
              <i
                className="fa fa-exclamation-circle ml-4 align-self-center"
                aria-hidden="true"
              ></i>
            )}
          </CurrentOrgLink>
        </li>
      </ResponsiveFirstLevelNav>
    </>
  );
};

const PageNotFound = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(changeOrg({ orgId: "default", ifPrevious: null }));
  });

  return (
    <h1 className="mt-6">
      <RPFormattedMessage
        id="pageNotFound"
        defaultMessage="Sivua ei löytynyt!"
      />
    </h1>
  );
};

const OrgNotFound = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(toggleOrgContext(false));
  });

  return (
    <>
      <h1>
        <RPFormattedMessage
          id="rpHeaderNoOrg"
          defaultMessage="Luotettava Kumppani tilaajavastuutiedot -palvelu"
        />
      </h1>
      <p>
        <RPFormattedMessage
          id="orgNotFound"
          defaultMessage="Yritystä ei löytynyt. Tarkista, että yrityksen tunniste on oikein ja että sinulla on käyttöoikeus yrityksen tietoihin."
        />
      </p>
    </>
  );
};

const OrgContent = () => {
  const dispatch = useAppDispatch();
  const { path } = useRouteMatch();
  const { orgId } = useParams<{ orgId: string }>();
  const userInfo = useUserInfo();

  let knownOrg = !!userInfo.data?.isAdmin;
  if (!knownOrg && userInfo.data?.companies) {
    knownOrg = userInfo.data?.companies
      .map(company => company.qvarnId)
      .includes(orgId);
  }

  useEffect(() => {
    dispatch(toggleOrgContext(knownOrg));
    return () => {
      dispatch(toggleOrgContext(false));
    };
  }, [dispatch, knownOrg]);

  useEffect(() => {
    dispatch(changeOrg({ orgId }));
  }, [dispatch, orgId]);

  return (
    <>
      <Switch>
        <Route path={`${path}/not-found`}>
          <OrgNotFound />
        </Route>
        <Route path={`${path}/details`}>
          <RPNavBar />
          <CompanyDetails />
        </Route>
        <Route exact path={`${path}/order`}>
          <OrderPage />
        </Route>
        <Route exact path={`${path}/permission`}>
          <PermissionPage />
        </Route>
        <Route exact path={path}>
          <RPNavBar />
          <Frontpage />
        </Route>
        <Route>
          <PageNotFound />
        </Route>
      </Switch>
    </>
  );
};

const RedirectToDefaultOrg = () => {
  const userInfoQuery = useUserInfo();
  const userCompanies = userInfoQuery.data?.companies;
  if (userCompanies) {
    let org = userCompanies.find(company => company.default);
    if (!org) {
      org = userCompanies[0];
    }
    return <Redirect to={`/orgs/${org.qvarnId}`} />; // nosemgrep typescript.react.security.audit.react-router-redirect.react-router-redirect
  }
  return <div className="page-loading-indicator" />;
};

const MainContent = () => {
  return (
    <Switch>
      <Route path="/orgs/default">
        <RedirectToDefaultOrg />
      </Route>
      <Route exact path={["/", "/orgs"]}>
        <RedirectToDefaultOrg />
      </Route>
      <Route path="/orgs/:orgId">
        <OrgContent />
      </Route>
      <Route>
        <PageNotFound />
      </Route>
    </Switch>
  );
};

export default MainContent;
